import React from "react";
import { motion } from "framer-motion";
import { X, Eye, Mail, Phone, MapPin, User, Shield, CreditCard, Globe, Calendar, DollarSign, TrendingUp, Star, Award } from "lucide-react";

const ViewPromoterModal = ({ promoter, onClose }) => {
  if (!promoter) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "inactive":
        return "text-gray-600 bg-gray-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case "excellent":
        return "text-purple-600 bg-purple-100";
      case "good":
        return "text-blue-600 bg-blue-100";
      case "average":
        return "text-orange-600 bg-orange-100";
      case "new":
        return "text-gray-600 bg-gray-100";
      case "inactive":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex gap-3 items-center">
            <div className="p-2 bg-[#FF6C6C] rounded-lg">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Promoter Details</h2>
              <p className="text-sm text-gray-600">View detailed promoter information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Basic Info */}
            <div className="space-y-6 lg:col-span-2">
              {/* Promoter Header */}
              <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9B9B]/10 rounded-xl border border-[#FF6C6C]/20">
                <div className="flex-shrink-0">
                  {promoter.profileImage ? (
                    <img
                      src={promoter.profileImage}
                      alt={`${promoter.first_name} ${promoter.last_name}`}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] flex items-center justify-center">
                      <span className="text-xl font-bold text-white">
                        {promoter.first_name?.charAt(0) || "?"}{promoter.last_name?.charAt(0) || ""}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">
                    {promoter.first_name} {promoter.last_name}
                  </h3>
                  {promoter.account_display_name && (
                    <p className="mb-2 text-sm text-blue-600">@{promoter.account_display_name}</p>
                  )}
                  <div className="flex gap-4 items-center mb-3 text-sm text-gray-600">
                    <div className="flex gap-1 items-center">
                      <Mail className="w-4 h-4" />
                      {promoter.email}
                    </div>
                    <div className="flex gap-1 items-center">
                      <Phone className="w-4 h-4" />
                      {promoter.phone_number}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(promoter.status)}`}>
                      {promoter.status}
                    </span>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPerformanceColor(promoter.performance)}`}>
                      {promoter.performance}
                    </span>
                    {promoter.rating > 0 && (
                      <div className="flex gap-1 items-center">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm font-medium">{promoter.rating}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex gap-2 items-center mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-600">Total Earnings</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">GH₵{promoter.totalEarnings.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex gap-2 items-center mb-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Active Deals</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{promoter.activeDeals}</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex gap-2 items-center mb-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-gray-600">Commission Rate</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{promoter.commissionRate}%</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <div className="flex gap-2 items-center mb-2">
                    <Calendar className="w-5 h-5 text-orange-600" />
                    <span className="text-sm font-medium text-gray-600">Join Date</span>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{new Date(promoter.joinDate).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Bio */}
              {promoter.bio && (
                <div className="p-6 bg-white rounded-xl border border-gray-200">
                  <h4 className="flex gap-2 items-center mb-3 text-lg font-semibold text-gray-900">
                    <User className="w-5 h-5" />
                    About
                  </h4>
                  <p className="leading-relaxed text-gray-700">{promoter.bio}</p>
                </div>
              )}

              {/* Performance Metrics */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <TrendingUp className="w-5 h-5" />
                  Performance Metrics
                </h4>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Deals</p>
                    <p className="text-lg font-bold text-gray-900">{promoter.totalDeals}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                    <p className="text-lg font-bold text-gray-900">{promoter.conversionRate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Payouts</p>
                    <p className="text-lg font-bold text-gray-900">GH₵{promoter.totalPayouts.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pending Payouts</p>
                    <p className="text-lg font-bold text-gray-900">GH₵{promoter.pendingPayouts.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Additional Info */}
            <div className="space-y-6">
              {/* Account Information */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <Shield className="w-5 h-5" />
                  Account Information
                </h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Verification Status</p>
                    <p className="font-medium text-gray-900 capitalize">{promoter.verificationStatus}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Last Active</p>
                    <p className="font-medium text-gray-900">{new Date(promoter.lastActive).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <div className="flex gap-1 items-center">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <p className="font-medium text-gray-900">
                        {promoter.location ? 
                          `${promoter.location.street_address_1 || ""}, ${promoter.location.city || ""}, ${promoter.location.region || ""}, ${promoter.location.country || ""}`.replace(/^,\s*|,\s*$/g, '') || "Not specified"
                        : "Not specified"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payout Information */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <CreditCard className="w-5 h-5" />
                  Payout Information
                </h4>
                {promoter.payoutInfo.network ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Network</p>
                      <p className="font-medium text-gray-900 capitalize">{promoter.payoutInfo.network}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <p className="font-medium text-gray-900">{promoter.payoutInfo.phoneNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Name</p>
                      <p className="font-medium text-gray-900">{promoter.payoutInfo.accountName}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No payout information configured</p>
                )}
              </div>

              {/* Social Media */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h4 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
                  <Globe className="w-5 h-5" />
                  Social Media
                </h4>
                <div className="space-y-2">
                  {promoter.socialMedia.facebook && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Facebook</span>
                      <span className="text-sm font-medium text-gray-900">{promoter.socialMedia.facebook}</span>
                    </div>
                  )}
                  {promoter.socialMedia.twitter && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Twitter</span>
                      <span className="text-sm font-medium text-gray-900">{promoter.socialMedia.twitter}</span>
                    </div>
                  )}
                  {promoter.socialMedia.instagram && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Instagram</span>
                      <span className="text-sm font-medium text-gray-900">{promoter.socialMedia.instagram}</span>
                    </div>
                  )}
                  {promoter.socialMedia.linkedin && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">LinkedIn</span>
                      <span className="text-sm font-medium text-gray-900">{promoter.socialMedia.linkedin}</span>
                    </div>
                  )}
                  {!promoter.socialMedia.facebook && !promoter.socialMedia.twitter && !promoter.socialMedia.instagram && !promoter.socialMedia.linkedin && (
                    <p className="text-sm text-gray-500">No social media links provided</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ViewPromoterModal;
